import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import {
    Users,
    IndianRupee,
    Activity,
    ArrowUpRight,
    Shield,
    FileText,
    TrendingUp,
    Zap,
    Briefcase,
    Package,
    Settings,
    CheckCircle,
    Clock,
    Sparkles
} from 'lucide-react';
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area
} from 'recharts';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white p-4 rounded-xl shadow-xl border border-gray-200">
                <p className="font-semibold text-gray-900 mb-3 text-sm">{label}</p>
                <div className="space-y-1.5">
                    {payload.map((entry, index) => (
                        <p key={index} className="text-xs font-medium" style={{ color: entry.color }}>
                            {entry.name}: {entry.value.toLocaleString('en-IN')}
                        </p>
                    ))}
                </div>
            </div>
        );
    }
    return null;
};

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch(`${API_BASE_URL}/admin/dashboard.php`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (data.success) {
                    setStats(data.stats);
                }
            } catch (error) {
                console.error("Error fetching admin stats:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    // Derived Variables
    const totalUsers = stats?.total_users || 0;
    const totalRevenue = stats?.total_revenue || 0;
    const pendingWithdrawals = stats?.pending_withdrawals || 0;
    const recentRegistrations = stats?.recent_registrations || [];
    const totalFranchises = stats?.total_franchises || 0;

    // Use actual data for charts if available
    const chartData = stats?.revenue_history || [
        { name: 'Mon', revenue: 0, payouts: 0 },
        { name: 'Tue', revenue: 0, payouts: 0 },
        { name: 'Wed', revenue: 0, payouts: 0 },
        { name: 'Thu', revenue: 0, payouts: 0 },
        { name: 'Fri', revenue: 0, payouts: 0 },
        { name: 'Sat', revenue: 0, payouts: 0 },
        { name: 'Sun', revenue: 0, payouts: 0 },
    ];

    const regData = stats?.registration_history || [
        { name: 'Mon', users: 0 },
        { name: 'Tue', users: 0 },
        { name: 'Wed', users: 0 },
        { name: 'Thu', users: 0 },
        { name: 'Fri', users: 0 },
        { name: 'Sat', users: 0 },
        { name: 'Sun', users: 0 },
    ];

    // Status Pie Chart
    const pieData = [
        { name: 'Total Users', value: parseInt(totalUsers) || 0 },
        { name: 'Franchises', value: parseInt(totalFranchises) || 0 }
    ];

    const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444'];

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-8">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-black bg-gradient-to-r from-gray-900 via-indigo-900 to-gray-900 bg-clip-text text-transparent tracking-tight">
                            Command Center
                        </h1>
                        <p className="text-gray-500 mt-1.5 text-sm sm:text-base font-medium">
                            Real-time platform insights and administrative controls
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden lg:flex items-center px-4 py-2 bg-indigo-50 text-indigo-700 rounded-xl border border-indigo-100 font-bold text-sm gap-2">
                            <Clock size={16} />
                            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <button className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl shadow-lg shadow-indigo-100 transition-all flex items-center gap-2 active:scale-95">
                            <Activity size={18} />
                            Platform Live
                        </button>
                    </div>
                </div>

                {/* Quick Actions Card Menu */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {[
                        { icon: Users, label: 'Add User', path: '/register', color: 'bg-blue-500' },
                        { icon: CheckCircle, label: 'KYC Sync', path: '/admin/approvals', color: 'bg-green-500' },
                        { icon: Package, label: 'Products', path: '/admin/products/list', color: 'bg-purple-500' },
                        { icon: Zap, label: 'E-Pins', path: '/admin/epin-requests', color: 'bg-orange-500' },
                        { icon: Briefcase, label: 'Franchise', path: '/admin/create-franchise', color: 'bg-cyan-500' },
                        { icon: Sparkles, label: 'Landing Showcase', path: '/admin/landing-products', color: 'bg-indigo-500' },
                        { icon: Settings, label: 'Settings', path: '/admin/settings', color: 'bg-gray-500' },
                    ].map((action, i) => (
                        <button
                            key={i}
                            onClick={() => navigate(action.path)}
                            className="bg-white p-4 rounded-2xl border border-gray-100 hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-50/50 transition-all group flex flex-col items-center gap-3 text-center"
                        >
                            <div className={`${action.color} p-3 rounded-xl shadow-lg shadow-gray-200 group-hover:scale-110 transition-transform`}>
                                <action.icon size={20} className="text-white" />
                            </div>
                            <span className="text-sm font-bold text-gray-700">{action.label}</span>
                        </button>
                    ))}
                </div>

                {/* Main Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {/* Total Users Card */}
                    <div className="group relative bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-2xl hover:border-purple-200 transition-all duration-300">
                        <div className="absolute top-0 right-0 w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-purple-400/10 to-indigo-500/10 rounded-bl-full"></div>
                        <div className="relative">
                            <div className="flex items-center justify-between mb-3 sm:mb-4">
                                <div className="p-2.5 sm:p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg shadow-purple-500/30">
                                    <Users size={20} className="text-white sm:hidden" />
                                    <Users size={24} className="text-white hidden sm:block" />
                                </div>
                                <span className="flex items-center text-[10px] sm:text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-lg">
                                    <ArrowUpRight size={12} className="mr-1 sm:hidden" />
                                    <ArrowUpRight size={14} className="mr-1 hidden sm:block" /> +12%
                                </span>
                            </div>
                            <p className="text-gray-500 text-[10px] sm:text-xs font-medium mb-1 uppercase tracking-wider">Total Users</p>
                            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{totalUsers.toLocaleString()}</h2>
                        </div>
                    </div>

                    {/* Revenue Card */}
                    <div className="group relative bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-2xl hover:border-green-200 transition-all duration-300">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-green-400/10 to-emerald-500/10 rounded-bl-full"></div>
                        <div className="relative">
                            <div className="flex items-center justify-between mb-4">
                                <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg shadow-green-500/30">
                                    <IndianRupee size={24} className="text-white" />
                                </div>
                                <span className="flex items-center text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-lg">
                                    <ArrowUpRight size={14} className="mr-1" /> +8.5%
                                </span>
                            </div>
                            <p className="text-gray-500 text-xs sm:text-sm font-medium mb-1">Total Revenue</p>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">₹{parseFloat(totalRevenue).toLocaleString('en-IN')}</h2>
                        </div>
                    </div>

                    {/* Pending KYC Card */}
                    <div className="group relative bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-2xl hover:border-orange-200 transition-all duration-300 cursor-pointer" onClick={() => navigate('/admin/approvals')}>
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-400/10 to-amber-500/10 rounded-bl-full"></div>
                        <div className="relative">
                            <div className="flex items-center justify-between mb-4">
                                <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl shadow-lg shadow-orange-500/30">
                                    <FileText size={24} className="text-white" />
                                </div>
                                <span className="flex items-center text-xs font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded-lg">
                                    Action Required
                                </span>
                            </div>
                            <p className="text-gray-500 text-xs sm:text-sm font-medium mb-1">Pending Withdrawals</p>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{pendingWithdrawals}</h2>
                        </div>
                    </div>

                    {/* Active Franchises Card */}
                    <div className="group relative bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-2xl hover:border-blue-200 transition-all duration-300">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-400/10 to-cyan-500/10 rounded-bl-full"></div>
                        <div className="relative">
                            <div className="flex items-center justify-between mb-4">
                                <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl shadow-lg shadow-blue-500/30">
                                    <Shield size={24} className="text-white" />
                                </div>
                                <span className="flex items-center text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-lg">
                                    Active
                                </span>
                            </div>
                            <p className="text-gray-500 text-xs sm:text-sm font-medium mb-1">Franchises</p>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{totalFranchises}</h2>
                        </div>
                    </div>
                </div>

                {/* Charts Area */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Revenue Chart */}
                    <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-gray-100 relative group overflow-hidden">
                        <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-500">
                            <TrendingUp size={120} className="text-indigo-600" />
                        </div>
                        <div className="flex items-center justify-between mb-8 relative z-10">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">Revenue Stream</h3>
                                <p className="text-gray-500 text-xs mt-0.5 font-medium">Daily package sales performance</p>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="hidden sm:flex items-center gap-4 text-xs font-bold mr-4">
                                    <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-indigo-500"></div>Revenue</div>
                                </div>
                                <select className="bg-gray-50 border border-gray-200 text-xs font-bold px-3 py-1.5 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 border-none">
                                    <option>Last 7 Days</option>
                                    <option disabled>Monthly</option>
                                </select>
                            </div>
                        </div>
                        <div className="h-[320px] w-full relative z-10">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                    <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} fontWeight="bold" tickLine={false} axisLine={false} dy={10} />
                                    <YAxis stroke="#94a3b8" fontSize={11} fontWeight="bold" tickLine={false} axisLine={false} tickFormatter={(val) => `₹${val}`} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" animationDuration={1500} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Bottom Charts Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-6">
                        {/* User Distribution Pie Chart */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col h-full">
                            <div className="mb-6">
                                <h3 className="text-xl font-bold text-gray-900">User Distribution</h3>
                                <p className="text-gray-500 text-xs mt-0.5 font-medium">Breakdown of member types</p>
                            </div>
                            <div className="flex-1 h-[250px] w-full flex justify-center items-center relative">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={70}
                                            outerRadius={90}
                                            paddingAngle={8}
                                            dataKey="value"
                                            stroke="none"
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                    <span className="text-2xl font-black text-gray-900">{(totalUsers + totalFranchises).toLocaleString()}</span>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Base</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-4">
                                {pieData.map((item, index) => (
                                    <div key={index} className="p-3 bg-gray-50 rounded-xl border border-gray-100 text-center">
                                        <div className="flex items-center justify-center gap-2 mb-1">
                                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index] }}></div>
                                            <span className="text-[10px] font-bold text-gray-400 uppercase">{item.name}</span>
                                        </div>
                                        <span className="text-sm font-black text-gray-900">{item.value.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Registration Trend Chart */}
                    <div className="lg:col-span-3 bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col group overflow-hidden relative">
                        <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-500">
                            <TrendingUp size={120} className="text-emerald-600" />
                        </div>
                        <div className="mb-8 relative z-10 flex items-center justify-between">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">Growth Projection</h3>
                                <p className="text-gray-500 text-xs mt-0.5 font-medium">New member acquisitions trend</p>
                            </div>
                            <div className="px-3 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-black rounded-lg border border-emerald-100 uppercase tracking-wider">
                                Trending Up
                            </div>
                        </div>
                        <div className="flex-1 h-[250px] w-full relative z-10">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={regData}>
                                    <defs>
                                        <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                    <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} fontWeight="bold" tickLine={false} axisLine={false} dy={10} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area type="monotone" dataKey="users" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorUsers)" animationDuration={1500} name="New Users" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Recent Registrations Section */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/30">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 italic">Recent Registrations</h3>
                            <p className="text-sm text-gray-500 font-medium">Newly onboarded growth partners</p>
                        </div>
                        <button
                            onClick={() => navigate('/admin/users')}
                            className="px-4 py-2 bg-indigo-50 text-indigo-700 text-xs font-black uppercase tracking-widest rounded-xl hover:bg-indigo-100 transition-all border border-indigo-100 shadow-sm shadow-indigo-50"
                        >
                            View Directory
                        </button>
                    </div>

                    {/* Desktop View */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50/50">
                                <tr className="text-gray-400 text-[10px] font-black uppercase tracking-[0.15em]">
                                    <th className="px-6 py-4">Growth Partner</th>
                                    <th className="px-6 py-4">Current Tier</th>
                                    <th className="px-6 py-4">Standing</th>
                                    <th className="px-6 py-4">Onboarding</th>
                                    <th className="px-6 py-4 text-right">Insight</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {recentRegistrations.length > 0 ? (
                                    recentRegistrations.map((user, index) => (
                                        <tr key={index} className="hover:bg-indigo-50/30 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-sm shadow-lg shadow-indigo-100 group-hover:scale-110 transition-transform">
                                                        {user.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-black text-gray-900 group-hover:text-indigo-600 transition-colors">{user.name}</p>
                                                        <p className="text-[10px] font-bold text-gray-400 font-mono">ID: {user.user_id}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="px-2.5 py-1 bg-yellow-50 text-yellow-700 rounded-lg text-[10px] font-black uppercase tracking-wider border border-yellow-100 shadow-sm italic">
                                                    {user.selected_package || user.package_name || 'TRIAL'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full shadow-sm ${user.package_status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}></div>
                                                    <span className="text-[11px] font-black text-gray-700 uppercase tracking-widest italic">{user.package_status || 'Inactive'}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-xs font-bold text-gray-500">
                                                {new Date(user.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all shadow-sm">
                                                    <Activity size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-12 text-center text-gray-400 italic">
                                            <div className="flex flex-col items-center gap-2">
                                                <Users size={40} className="text-gray-200" />
                                                <p className="font-bold text-sm">Waiting for new growth partners...</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile View - Cards */}
                    <div className="md:hidden divide-y divide-gray-50">
                        {recentRegistrations.length > 0 ? (
                            recentRegistrations.map((user, index) => (
                                <div key={index} className="p-5 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-lg shadow-lg shadow-indigo-100">
                                                {user.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <p className="font-black text-gray-900">{user.name}</p>
                                                <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest italic">{user.user_id}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{new Date(user.created_at).toLocaleDateString()}</p>
                                            <div className="mt-1">
                                                <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider border ${user.package_status === 'active' ? 'bg-green-50 text-green-600 border-green-100' : 'bg-gray-50 text-gray-400 border-gray-100'}`}>
                                                    {user.package_status || 'Inactive'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between bg-gray-50/50 p-3 rounded-xl border border-gray-100">
                                        <div>
                                            <span className="text-[9px] font-black text-gray-400 uppercase tracking-[0.15em] block mb-0.5">Assigned Tier</span>
                                            <span className="text-xs font-black text-gray-700 italic">{user.selected_package || user.package_name || 'TRIAL'}</span>
                                        </div>
                                        <button className="p-2.5 bg-white text-indigo-600 rounded-xl shadow-sm border border-indigo-50 active:scale-95 transition-all">
                                            <ArrowUpRight size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-12 text-center">
                                <Users size={32} className="mx-auto text-gray-200 mb-2" />
                                <p className="text-sm font-bold text-gray-400 italic">Platform expansion in progress...</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default AdminDashboard;
